import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Input,
  Label,
  Button,
  Badge,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@forgenetwork/ui';
import { useToast } from '@/shared/hooks/useToast';
import { api } from '@/lib/api';
import { Settings as SettingsIcon, CreditCard, AlertTriangle, Zap, Loader2 } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});

interface Workspace {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  ownerId: string;
  userRole: 'owner' | 'admin' | 'member' | 'viewer';
}

interface Subscription {
  id: string;
  status: 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'paused';
  planId: string;
  planName?: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

const workspaceSchema = z.object({
  name: z.string().min(1, 'Workspace name is required').max(100),
  description: z.string().max(500).optional(),
});

type WorkspaceFormData = z.infer<typeof workspaceSchema>;

function SettingsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = React.useState('');

  // Fetch workspaces
  const { data: workspacesData, isLoading } = useQuery<{ workspaces: Workspace[] }>({
    queryKey: ['workspaces'],
    queryFn: async () => api.get('/workspaces'),
  });

  // Fetch subscription for billing tab
  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery<{
    subscription: Subscription | null;
  }>({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => api.get('/billing/subscription'),
  });

  const subscription = subscriptionData?.subscription;
  const hasActiveSubscription = subscription && ['active', 'trialing'].includes(subscription.status);

  const workspace = workspacesData?.workspaces?.[0];

  const getPlanDisplayName = (sub: Subscription): string => {
    if (sub.planName) {
      const name = sub.planName.toLowerCase();
      if (name === 'pro') return 'Pro Plan';
      if (name === 'enterprise') return 'Enterprise Plan';
      if (name === 'free' || name === 'starter') return 'Starter Plan';
      return sub.planName.charAt(0).toUpperCase() + sub.planName.slice(1) + ' Plan';
    }
    if (sub.planId.includes('pro')) return 'Pro Plan';
    if (sub.planId.includes('enterprise')) return 'Enterprise Plan';
    return 'Starter Plan';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [errors, setErrors] = React.useState<{ name?: string; description?: string }>({});

  // Initialize form when workspace loads
  React.useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setDescription(workspace.description ?? '');
    }
  }, [workspace]);

  // Update workspace mutation
  const updateWorkspaceMutation = useMutation({
    mutationFn: async (data: WorkspaceFormData) => {
      return api.patch(`/workspaces/${workspace?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast({
        title: 'Success',
        description: 'Workspace settings updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update workspace',
        variant: 'destructive',
      });
    },
  });

  // Delete workspace mutation
  const deleteWorkspaceMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/workspaces/${workspace?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast({
        title: 'Success',
        description: 'Workspace deleted successfully',
      });
      navigate({ to: '/dashboard' });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete workspace',
        variant: 'destructive',
      });
    },
  });

  const handleUpdateWorkspace = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = workspaceSchema.safeParse({ name, description });

    if (!result.success) {
      const fieldErrors: { name?: string; description?: string } = {};
      result.error.issues.forEach((issue) => {
        if (issue.path[0] === 'name') {
          fieldErrors.name = issue.message;
        } else if (issue.path[0] === 'description') {
          fieldErrors.description = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    updateWorkspaceMutation.mutate(result.data);
  };

  const handleDeleteWorkspace = () => {
    if (deleteConfirmation !== workspace?.name) {
      toast({
        title: 'Error',
        description: 'Workspace name does not match',
        variant: 'destructive',
      });
      return;
    }

    deleteWorkspaceMutation.mutate();
  };

  const isOwner = workspace?.userRole === 'owner';

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-8 text-muted-foreground">Loading settings...</div>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>No Workspace Found</CardTitle>
              <CardDescription>
                You need to create a workspace first to access settings.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage workspace settings and preferences
          </p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList>
            <TabsTrigger value="general">
              <SettingsIcon className="mr-2 h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="billing">
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </TabsTrigger>
            {isOwner && (
              <TabsTrigger value="danger">
                <AlertTriangle className="mr-2 h-4 w-4" />
                Danger Zone
              </TabsTrigger>
            )}
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <Card>
              <form onSubmit={handleUpdateWorkspace}>
                <CardHeader>
                  <CardTitle>Workspace Settings</CardTitle>
                  <CardDescription>
                    Update your workspace name and description
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Workspace Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={!isOwner || updateWorkspaceMutation.isPending}
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive">{errors.name}</p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="slug">Workspace Slug</Label>
                    <Input
                      id="slug"
                      value={workspace.slug}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Auto-generated from workspace name
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={!isOwner || updateWorkspaceMutation.isPending}
                      placeholder="What is this workspace for?"
                      className={errors.description ? 'border-destructive' : ''}
                    />
                    {errors.description && (
                      <p className="text-sm text-destructive">{errors.description}</p>
                    )}
                  </div>

                  {!isOwner && (
                    <div className="text-sm text-muted-foreground p-3 border rounded-lg bg-muted/50">
                      Only workspace owners can update these settings
                    </div>
                  )}
                </CardContent>
                {isOwner && (
                  <CardFooter>
                    <Button
                      type="submit"
                      disabled={updateWorkspaceMutation.isPending}
                    >
                      {updateWorkspaceMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </CardFooter>
                )}
              </form>
            </Card>
          </TabsContent>

          {/* Billing */}
          <TabsContent value="billing">
            <Card>
              <CardHeader>
                <CardTitle>Billing & Subscription</CardTitle>
                <CardDescription>
                  Manage your subscription and billing information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscriptionLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : hasActiveSubscription ? (
                  <>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/60">
                          <Zap className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{getPlanDisplayName(subscription)}</div>
                          <div className="text-sm text-muted-foreground">
                            {subscription.cancelAtPeriodEnd ? (
                              <>Cancels on {formatDate(subscription.currentPeriodEnd)}</>
                            ) : (
                              <>Renews on {formatDate(subscription.currentPeriodEnd)}</>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge variant="default">
                        {subscription.status === 'trialing' ? 'Trial' : 'Active'}
                      </Badge>
                    </div>

                    <Button variant="outline" className="w-full" onClick={() => navigate({ to: '/billing' })}>
                      Manage Subscription
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-semibold">Starter Plan</div>
                        <div className="text-sm text-muted-foreground">
                          Limited features with basic functionality
                        </div>
                      </div>
                      <Badge variant="outline">Free</Badge>
                    </div>

                    <div className="space-y-3">
                      <Button variant="default" className="w-full" onClick={() => navigate({ to: '/billing' })}>
                        Upgrade to Pro
                      </Button>
                      <Button variant="outline" className="w-full" onClick={() => navigate({ to: '/billing' })}>
                        Manage Billing Portal
                      </Button>
                    </div>

                    <div className="text-sm text-muted-foreground p-3 border rounded-lg bg-muted/50">
                      Pro features include advanced analytics, unlimited projects, and priority support.
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Danger Zone */}
          {isOwner && (
            <TabsContent value="danger">
              <Card className="border-destructive">
                <CardHeader>
                  <CardTitle className="text-destructive">Danger Zone</CardTitle>
                  <CardDescription>
                    Irreversible and destructive actions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="w-full">
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Delete Workspace
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Workspace</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete the
                          workspace and all associated data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-4">
                        <Label htmlFor="delete-confirm">
                          Type <span className="font-mono font-bold">{workspace.name}</span> to confirm
                        </Label>
                        <Input
                          id="delete-confirm"
                          value={deleteConfirmation}
                          onChange={(e) => setDeleteConfirmation(e.target.value)}
                          placeholder={workspace.name}
                          className="mt-2"
                        />
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteConfirmation('')}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteWorkspace}
                          disabled={deleteConfirmation !== workspace.name || deleteWorkspaceMutation.isPending}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {deleteWorkspaceMutation.isPending ? 'Deleting...' : 'Delete Workspace'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <p className="text-sm text-muted-foreground mt-4">
                    Once deleted, the workspace and all its data will be gone forever.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
